import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { StateCard } from '../../components/StateCard';
import { Empty, SiteHead, formatDate } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

type SectionItem = string | { title: string; body?: string; href?: string };

interface Section {
  heading?: string;
  body?: string;
  items?: SectionItem[];
  action?: { label: string; path: string } | null;
}

interface CompanyPayload {
  key: string;
  published: boolean;
  title: string | null;
  body?: string | null;
  lead?: string | null;
  sections: Section[];
  contact?: { label?: string; path?: string; email?: string; note?: string } | null;
  updated_at?: string | null;
  note?: string;
}

const META: Record<string, { eyebrow: string; fallbackTitle: string; lead: string }> = {
  about: { eyebrow: '소개', fallbackTitle: 'ASKRS Games', lead: '게임과 소식' },
  press: { eyebrow: '보도·브랜드', fallbackTitle: '보도·브랜드', lead: '보도 문의와 브랜드 자산 사용 기준' },
  careers: { eyebrow: '채용', fallbackTitle: '채용', lead: '현재 모집 중인 공고가 없어요.' },
  contact: { eyebrow: '연락처', fallbackTitle: '연락처', lead: '고객지원·보도·제휴 문의' },
};

/** P021–P024 — company pages, backed by the same publishing pipeline as game content. */
export function CompanyPage({ pageKey }: { pageKey: string }) {
  const meta = META[pageKey] ?? { eyebrow: 'ASKRS', fallbackTitle: pageKey, lead: '' };

  const page = useResource<CompanyPayload>(
    (signal) => api.public(`/api/v1/site/pages/${pageKey}`, { signal }) as Promise<ApiEnvelope<CompanyPayload>>,
    [pageKey],
    { emptyWhen: () => false },
  );

  const data = page.data;

  return (
    <StateCard state={page.state} error={page.error} onRetry={page.reload}>
      {data && (
        <>
          <SiteHead
            eyebrow={meta.eyebrow}
            title={data.title ?? meta.fallbackTitle}
            lead={data.lead ?? meta.lead}
            meta={data.published && data.updated_at ? <span>업데이트 {formatDate(data.updated_at)}</span> : undefined}
          />

          {!data.published ? (
            <Empty
              title="아직 공개되지 않았어요"
              body={data.note ?? '아직 공개되지 않은 페이지예요.'}
              action={<Link className="btn" to="/">홈으로</Link>}
            />
          ) : (
            <>
              {data.body && <div className="article__body" style={{ marginBottom: 36 }}>{data.body}</div>}

              {data.sections.map((section, index) => (
                <section key={index} className="section" style={{ marginBottom: 36 }}>
                  {section.heading && <h2 className="section__title">{section.heading}</h2>}
                  {section.body && <p style={{ lineHeight: 1.85, margin: '0 0 16px', color: 'var(--text-secondary)' }}>{section.body}</p>}

                  {section.items && section.items.length > 0 && (
                    <ul className="bullet-list">
                      {section.items.map((item, itemIndex) => (
                        typeof item === 'string'
                          ? <li key={itemIndex}>{item}</li>
                          : (
                            <li key={itemIndex}>
                              <strong>{item.title}</strong>
                              {item.body && <><br />{item.body}</>}
                              {item.href && (
                                <>
                                  {' '}
                                  {item.href.startsWith('http')
                                    ? <a href={item.href} target="_blank" rel="noopener noreferrer">자세히</a>
                                    : <Link to={item.href}>자세히</Link>}
                                </>
                              )}
                            </li>
                          )
                      ))}
                    </ul>
                  )}

                  {section.action && (
                    <p style={{ margin: '16px 0 0' }}>
                      <Link className="btn" to={section.action.path}>{section.action.label}</Link>
                    </p>
                  )}
                </section>
              ))}

              {data.contact && (data.contact.email || data.contact.path) && (
                <section className="card">
                  <h2 className="section__title" style={{ marginBottom: 8 }}>연락처</h2>
                  {data.contact.email && <p style={{ margin: '0 0 12px' }}>{data.contact.email}</p>}
                  {data.contact.path && (
                    <Link className="btn" to={data.contact.path}>{data.contact.label ?? '문의하기'}</Link>
                  )}
                  {data.contact.note && <p style={{ margin: '12px 0 0', color: 'var(--text-tertiary)' }}>{data.contact.note}</p>}
                </section>
              )}

              {pageKey === 'contact' && (
                <div className="chips" style={{ marginTop: 24 }}>
                  <Link className="chip" to="/support/new">게임 문의</Link>
                  <Link className="chip" to="/privacy/requests">내 데이터 요청</Link>
                  <Link className="chip" to="/status">서비스 상태</Link>
                </div>
              )}
            </>
          )}
        </>
      )}
    </StateCard>
  );
}
